import React from 'react';
import PropTypes from 'prop-types';
import kebabCase from 'lodash/kebabCase';
import { FormattedMessage } from 'react-intl';
import { availabilityShape, libraryShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import ILSOfflineMessage from 'app/components/bibs/shared/ILSOfflineMessage';
import AvailabilityStatus from 'app/components/shared/AvailabilityStatus';
import './CirculationInfo.scss';

function renderCirculationCount(key, count) {
  const keysWithPluralVariant = ['volumes', 'total_copies'];
  const values = <span className="circulation-count">{count}</span>;
  const suffix = !keysWithPluralVariant.includes(key) || count === 1 ? 'singular' : 'plural';
  return (
    <div>
      <span aria-hidden className={`${kebabCase(key)}-count`}>
        <FormattedMessage id={`circ_${key}_${suffix}`} values={{ count: values }} />
      </span>
      <ScreenReaderMessage>
        <FormattedMessage id={`circ_${key}_${suffix}`} values={{ count }} />
      </ScreenReaderMessage>
    </div>
  );
}

export default function CirculationInfo({ availabilityError, realtimeAvailability, currentLibrary }) {
  const statusType = realtimeAvailability.get('statusType');
  const totalCopies = realtimeAvailability.get('totalCopies');
  const volumesCount = realtimeAvailability.get('volumesCount');
  const availableCopies = realtimeAvailability.get('availableCopies');
  const onOrderCopies = realtimeAvailability.get('onOrderCopies');
  const heldCopies = realtimeAvailability.get('heldCopies');
  const alwaysAvailable = realtimeAvailability.get('localisedStatus') === 'DIGITAL_ALWAYS_AVAILABLE';
  const hideHolds = currentLibrary.get('hideHolds');

  return (
    <div className="cp-circulation-info">
      <span className="availability-status">
        <AvailabilityStatus
          rollUpStatus={realtimeAvailability.get('statusType')}
          status={realtimeAvailability.get('localisedStatus')}
          availabilityLocationType={realtimeAvailability.get('availabilityLocationType')}
        />
      </span>
      <ILSOfflineMessage availabilityError={availabilityError} />
      <div className="circulation-details">
        {alwaysAvailable ? (
          <FormattedMessage id="title_always_available" tagName="span" />
        ) : (
          <>
            {totalCopies && statusType !== 'ON_ORDER' ? renderCirculationCount('total_copies', totalCopies) : null}
            {volumesCount ? renderCirculationCount('volumes', volumesCount) : null}
            {availableCopies ? renderCirculationCount('available', availableCopies) : null}
            {onOrderCopies ? renderCirculationCount('on_order', onOrderCopies) : null}
            {hideHolds ? null : renderCirculationCount('on_hold', heldCopies)}
          </>
        )}
      </div>
    </div>
  );
}

CirculationInfo.propTypes = {
  availabilityError: PropTypes.string,
  realtimeAvailability: availabilityShape.isRequired,
  currentLibrary: libraryShape.isRequired
};
